import {
  Box, Grid, Heading, Spinner, Table, Text, VStack, HStack, Badge, Tooltip, useBreakpointValue
} from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell
} from 'recharts'
import http from '../../utils/http'

interface ConversionRates {
  sessionToAssessment: number
  assessmentToBookingStarted: number
  bookingStartedToBooking: number
  bookingAbandonment: number
  assessmentToBooking: number
  overall: number
}

interface GrantMetrics {
  eligibilityRate: number
  totalPotentialValue: number
  averageValue: number
  eligibleHomes: number
}

interface TimeSeriesEntry {
  date: string
  sessions: number
  assessments: number
  bookings: number
  failed: number
}

interface GeographicEntry {
  district: string
  sessions: number
  assessments: number
  bookings: number
}

interface FinancialPostcodeEntry {
  district: string
  homes: number
  homesWithSavings: number
  homesWithRoi: number
  totalAnnualSavings: number
  averageAnnualSavings: number | null
  roi: number | null
}

interface FinancialBreakdown {
  totalAnnualSavings: number
  averageAnnualSavings: number | null
  roi: number | null
  homesWithSavings: number
  homesWithRoi: number
  postcodeBreakdown: FinancialPostcodeEntry[]
}

interface HomeCharacteristics {
  epcDistribution: Record<string, number>
  propertyTypeDistribution: Record<string, number>
  fuelDistribution: Record<string, number>
  constructionAgeDistribution: Record<string, number>
}

interface Engagement {
  uniqueVisitors: number
  bounceRate: number
  hourDistribution: Record<string, number>
  dayDistribution: Record<string, number>
}

interface GrantDetail {
  name: string
  description: string
  count: number
}

interface PilotAnalytics {
  totalSessions: number
  totalAssessments: number
  totalBookings: number
  totalBookingStarts: number
  bookingsAbandoned: number
  postcodesBlocked: number
  conversionRates: ConversionRates
  grantEligibilityBreakdown: Record<string, number>
  grantDetails?: GrantDetail[]
  grantMetrics: GrantMetrics
  timeSeries: TimeSeriesEntry[]
  geographicBreakdown: GeographicEntry[]
  financialBreakdown?: FinancialBreakdown
  homeCharacteristics: HomeCharacteristics
  engagement: Engagement
  dateRange: { from: string | null; to: string | null }
}

const CHART_COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658']
const EPC_COLORS: Record<string, string> = {
  'A': '#00a65a', 'B': '#4cb848', 'C': '#c6d21f', 'D': '#f5b036',
  'E': '#f28b2c', 'F': '#e15326', 'G': '#c32026',
}
type ActivityGranularity = 'daily' | 'weekly' | 'monthly'
const ACTIVITY_GRANULARITY_OPTIONS: Array<{ value: ActivityGranularity; label: string }> = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
]

const parseSeriesDate = (value: string): Date => {
  const [year, month, day] = value.split('-').map(Number)
  return new Date(year, month - 1, day)
}

const formatDateKey = (date: Date): string => (
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
)

const getBucketDate = (date: Date, granularity: ActivityGranularity): Date => {
  const bucket = new Date(date)
  bucket.setHours(0, 0, 0, 0)

  if (granularity === 'weekly') {
    const day = bucket.getDay()
    const daysSinceMonday = (day + 6) % 7
    bucket.setDate(bucket.getDate() - daysSinceMonday)
  }

  if (granularity === 'monthly') {
    bucket.setDate(1)
  }

  return bucket
}

const aggregateTimeSeries = (
  entries: TimeSeriesEntry[],
  granularity: ActivityGranularity,
): TimeSeriesEntry[] => {
  if (granularity === 'daily') {
    return entries
  }

  const buckets = new Map<string, TimeSeriesEntry>()

  entries.forEach(entry => {
    const bucketKey = formatDateKey(getBucketDate(parseSeriesDate(entry.date), granularity))
    const existing = buckets.get(bucketKey)

    if (existing) {
      existing.sessions += entry.sessions
      existing.assessments += entry.assessments
      existing.bookings += entry.bookings
      existing.failed += entry.failed
      return
    }

    buckets.set(bucketKey, {
      date: bucketKey,
      sessions: entry.sessions,
      assessments: entry.assessments,
      bookings: entry.bookings,
      failed: entry.failed,
    })
  })

  return Array.from(buckets.values()).sort((a, b) => a.date.localeCompare(b.date))
}

const formatActivityTick = (value: string, granularity: ActivityGranularity): string => {
  const date = parseSeriesDate(value)

  if (granularity === 'monthly') {
    return date.toLocaleDateString(undefined, { month: 'short', year: '2-digit' })
  }

  return `${date.getDate()}/${date.getMonth() + 1}`
}

const formatActivityTooltipLabel = (value: string, granularity: ActivityGranularity): string => {
  const date = parseSeriesDate(value)

  if (granularity === 'weekly') {
    return `Week of ${date.toLocaleDateString()}`
  }

  if (granularity === 'monthly') {
    return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
  }

  return date.toLocaleDateString()
}

const formatCurrency = (value: number | null | undefined): string => {
  if (value == null) return '–'

  const roundedValue = Math.round(value)
  const sign = roundedValue < 0 ? '-' : ''
  return `${sign}£${Math.abs(roundedValue).toLocaleString()}`
}

const formatRoi = (value: number | null | undefined): string => (
  value == null ? '–' : `${value.toFixed(1)}%`
)

const getRoiColor = (value: number | null | undefined): string => {
  if (value == null) return 'gray.500'
  if (value >= 8) return 'green.600'
  if (value >= 4) return 'orange.500'
  return 'gray.700'
}

function HelpIcon({ tooltip }: { tooltip: string }) {
  return (
    <Tooltip.Root openDelay={200} closeDelay={100}>
      <Tooltip.Trigger asChild>
        <Box
          as="span"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          w="14px"
          h="14px"
          borderRadius="full"
          bg="gray.200"
          color="gray.500"
          fontSize="10px"
          fontWeight="bold"
          cursor="help"
          ml={1}
          _hover={{ bg: 'gray.300' }}
        >
          ?
        </Box>
      </Tooltip.Trigger>
      <Tooltip.Positioner>
        <Tooltip.Content maxW="260px" fontSize="sm">{tooltip}</Tooltip.Content>
      </Tooltip.Positioner>
    </Tooltip.Root>
  )
}

function StatCard({ label, value, subtext, tooltip }: { label: string; value: string | number; subtext?: string; tooltip?: string }) {
  return (
    <Box bg="white" borderRadius="xl" p={{ base: 4, sm: 5 }} border="1px solid" borderColor="gray.200">
      <HStack gap={1} mb={1}>
        <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide">{label}</Text>
        {tooltip && <HelpIcon tooltip={tooltip} />}
      </HStack>
      <Text fontSize={{ base: '2xl', sm: '3xl' }} fontWeight="bold" color="gray.800">{typeof value === 'number' ? value.toLocaleString() : value}</Text>
      {subtext && <Text fontSize="sm" color="gray.500" mt={1}>{subtext}</Text>}
    </Box>
  )
}

function FunnelCard({ label, value, color, caption, tooltip }: {
  label: string
  value?: number
  color: string
  caption: string
  tooltip: string
}) {
  return (
    <Box bg="white" borderRadius="xl" p={{ base: 4, sm: 5 }} border="1px solid" borderColor="gray.200">
      <HStack gap={1} mb={1}>
        <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide">{label}</Text>
        <HelpIcon tooltip={tooltip} />
      </HStack>
      <HStack align="baseline" gap={2}>
        <Text fontSize="3xl" fontWeight="bold" color={color}>{value || 0}%</Text>
        <Text fontSize="sm" color="gray.500">{caption}</Text>
      </HStack>
    </Box>
  )
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <Heading size="md" color="gray.700" mt={{ base: 6, md: 8 }} mb={4}>{children}</Heading>
}

// humps camelizes object keys, so distribution keys like "Mid-Terrace" become "midTerrace".
// This reverses that for display: "midTerrace" → "Mid-Terrace", "semiDetached" → "Semi-Detached".
function decamelizeDistributionKeys(dist: Record<string, number>): Record<string, number> {
  const result: Record<string, number> = {}
  for (const [key, val] of Object.entries(dist)) {
    const normalized = key
      .replace(/([A-Z])/g, '-$1')
      .replace(/^-/, '')
      .split('-')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join('-')
    result[normalized] = val
  }
  return result
}

function DistributionPieChart({ data, title, colorKey, tooltip, allLabels, excludeLabels }: { data: Record<string, number>; title: string; colorKey?: Record<string, string>; tooltip?: string; allLabels?: string[]; excludeLabels?: string[] }) {
  const isMobile = useBreakpointValue({ base: true, sm: false }) ?? true
  const exclude = new Set((excludeLabels ?? []).map(k => k.toLowerCase()))

  // Normalize incoming data keys against allLabels (case-insensitive merge).
  const normalizedData: Record<string, number> = {}
  for (const [key, val] of Object.entries(data)) {
    const canonical = allLabels?.find(l => l.toLowerCase() === key.toLowerCase()) ?? key
    normalizedData[canonical] = (normalizedData[canonical] || 0) + val
  }

  let labels: string[]
  if (allLabels) {
    const extra = Object.keys(normalizedData).filter(k => !allLabels.find(l => l.toLowerCase() === k.toLowerCase()))
    labels = [...allLabels, ...extra].filter(k => !exclude.has(k.toLowerCase()))
  } else {
    labels = Object.keys(normalizedData)
      .filter(k => !exclude.has(k.toLowerCase()))
      .sort((a, b) => (normalizedData[b] || 0) - (normalizedData[a] || 0))
  }
  // For the pie only show slices with value > 0
  const chartData = labels
    .map(name => ({ name, value: normalizedData[name] || 0 }))
    .filter(d => d.value > 0)

  const getColor = (name: string, index: number) =>
    colorKey ? (colorKey[name] || CHART_COLORS[index % CHART_COLORS.length]) : CHART_COLORS[index % CHART_COLORS.length]

  return (
    <Box bg="white" borderRadius="xl" p={{ base: 4, sm: 6 }} border="1px solid" borderColor="gray.200">
      <HStack gap={1} mb={4}>
        <Text fontWeight="semibold" color="gray.700">{title}</Text>
        {tooltip && <HelpIcon tooltip={tooltip} />}
      </HStack>
      <ResponsiveContainer width="100%" height={isMobile ? 320 : 260}>
        <PieChart>
          <Pie
            data={chartData}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy={isMobile ? '38%' : '45%'}
            outerRadius={isMobile ? 72 : 90}
            label={({ name, percent }) => percent > 0.04 ? `${(percent * 100).toFixed(0)}%` : ''}
            labelLine={false}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getColor(entry.name, index)} />
            ))}
          </Pie>
          <RechartsTooltip formatter={(value: number, name: string) => [value, name]} />
          <Legend
            layout={isMobile ? 'horizontal' : 'vertical'}
            align={isMobile ? 'center' : 'right'}
            verticalAlign={isMobile ? 'bottom' : 'middle'}
            iconType="circle"
            iconSize={10}
            formatter={(value) => <span style={{ fontSize: 12, color: '#4A5568' }}>{value}</span>}
          />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  )
}

const PilotDashboard: React.FC = () => {
  const { council } = useParams<{ council: string }>()
  const [activityGranularity, setActivityGranularity] = useState<ActivityGranularity>('daily')

  const { data, isLoading, isError } = useQuery<PilotAnalytics>({
    queryKey: ['pilotAnalytics', council],
    queryFn: async () => {
      const res = await http.get(`/api/pilot/${council}/analytics/`)
      return res.data
    },
    enabled: !!council,
  })

  if (isLoading) {
    return (
      <Box p={10} display="flex" justifyContent="center">
        <Spinner size="xl" />
      </Box>
    )
  }

  if (isError || !data) {
    return (
      <Box p={{ base: 4, sm: 8 }}>
        <Text color="red.500">Failed to load analytics. Make sure you are logged in as a pilot org user.</Text>
      </Box>
    )
  }

  const grantBreakdown = Object.entries(data.grantEligibilityBreakdown ?? {})
  const hasTimeSeries = data.timeSeries && data.timeSeries.length > 0
  const hasGeographic = data.geographicBreakdown && data.geographicBreakdown.length > 0
  const hasHomeData = data.homeCharacteristics && Object.keys(data.homeCharacteristics.epcDistribution || {}).length > 0
  const financialBreakdown = data.financialBreakdown
  const hasFinancialBreakdown = (financialBreakdown?.postcodeBreakdown?.length ?? 0) > 0
  const activityTimeSeries = aggregateTimeSeries(data.timeSeries ?? [], activityGranularity)

  // Prepare hour distribution for chart
  const hourData = Object.entries(data.engagement?.hourDistribution || {})
    .map(([hour, count]) => ({ hour: `${hour}:00`, count }))
    .sort((a, b) => parseInt(a.hour) - parseInt(b.hour))

  // Prepare day distribution for chart
  const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
  const dayDist = data.engagement?.dayDistribution ?? {}
  const dayData = dayOrder.map(day => ({
    day: day.slice(0, 3),
    count: dayDist[day] || dayDist[day.toLowerCase()] || 0
  }))

  return (
    <Box maxW="1200px" mx="auto" py={{ base: 6, md: 10 }} px={{ base: 4, sm: 6 }}>
      <VStack gap={2} align="stretch">
        <Heading size={{ base: 'md', sm: 'lg' }} color="gray.800">Pilot Analytics Dashboard</Heading>
        <Text color="gray.500" mb={4}>
          {council && council.charAt(0).toUpperCase() + council.slice(1)} pilot programme performance
        </Text>

        {/* Core Stats */}
        <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }} gap={4}>
          <StatCard label="Total sessions" value={data.totalSessions} />
          <StatCard label="Assessments completed" value={data.totalAssessments} />
          <StatCard label="Price estimate leads" value={data.totalBookings} />
          <StatCard
            label="Lead forms started"
            value={data.totalBookingStarts ?? 0}
            subtext={`${data.bookingsAbandoned ?? 0} abandoned before submitting`}
            tooltip="Users who opened the price estimate form after their assessment. Abandoned means they opened it but never submitted."
          />
          <StatCard
            label="Out-of-area attempts"
            value={data.postcodesBlocked}
            tooltip="Users who tried to use the tool but their postcode was outside the pilot coverage area"
          />
        </Grid>

        {/* Conversion Funnel */}
        <SectionHeading>Conversion Funnel</SectionHeading>
        <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }} gap={4}>
          <FunnelCard
            label="Session → Assessment"
            value={data.conversionRates?.sessionToAssessment}
            color="blue.600"
            caption="conversion"
            tooltip="Percentage of visitors who completed a home assessment after starting a session"
          />
          <FunnelCard
            label="Assessment → Lead form"
            value={data.conversionRates?.assessmentToBookingStarted}
            color="teal.600"
            caption="opened the form"
            tooltip="Percentage of users who saw their assessment results and then opened the price estimate form"
          />
          <FunnelCard
            label="Lead form → Lead"
            value={data.conversionRates?.bookingStartedToBooking}
            color="green.600"
            caption={`completed (${data.conversionRates?.bookingAbandonment ?? 0}% abandoned)`}
            tooltip="Percentage of users who opened the price estimate form and went on to submit it. The rest abandoned the form."
          />
          <FunnelCard
            label="Overall Conversion"
            value={data.conversionRates?.overall}
            color="purple.600"
            caption="session → lead"
            tooltip="Percentage of all visitors who ultimately submitted their details to get price estimates (end-to-end conversion)"
          />
        </Grid>

        {/* Grant Metrics */}
        <SectionHeading>Grant Impact</SectionHeading>
        <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }} gap={4}>
          <StatCard
            label="Total potential value"
            value={`£${(data.grantMetrics?.totalPotentialValue || 0).toLocaleString()}`}
            subtext="max grant funding identified"
            tooltip="Sum of maximum grant amounts for all eligible homes. Each home counts their highest-value grant only."
          />
          <StatCard
            label="Avg grant value"
            value={`£${(data.grantMetrics?.averageValue || 0).toLocaleString()}`}
            subtext="per eligible home"
            tooltip="Average maximum grant amount available per eligible home"
          />
          <StatCard
            label="Eligibility rate"
            value={`${data.grantMetrics?.eligibilityRate || 0}%`}
            subtext="of assessed homes qualify"
            tooltip="Percentage of assessed homes that qualify for at least one grant based on EPC rating, fuel type, and other criteria"
          />
          <StatCard
            label="Eligible homes"
            value={data.grantMetrics?.eligibleHomes || 0}
            subtext="qualify for grants"
          />
        </Grid>

        {(data.grantDetails?.length ?? grantBreakdown.length) > 0 && (
          <Box bg="white" borderRadius="xl" p={{ base: 4, sm: 6 }} border="1px solid" borderColor="gray.200" mt={4}>
            <Text fontWeight="semibold" color="gray.700" mb={4}>Grant eligibility breakdown</Text>
            <Box overflowX="auto">
              <Table.Root size="sm" minW="400px">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>Grant</Table.ColumnHeader>
                    <Table.ColumnHeader textAlign="right">Eligible homes</Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {data.grantDetails
                    ? data.grantDetails.map(({ name, description, count }) => (
                      <Table.Row key={name}>
                        <Table.Cell>
                          <Text fontWeight="medium" color="gray.800">{name}</Text>
                          {description && (
                            <Text fontSize="xs" color="gray.500" mt={0.5}>{description}</Text>
                          )}
                        </Table.Cell>
                        <Table.Cell textAlign="right" fontWeight="semibold">{count}</Table.Cell>
                      </Table.Row>
                    ))
                    : grantBreakdown.map(([name, count]) => (
                      <Table.Row key={name}>
                        <Table.Cell>{name}</Table.Cell>
                        <Table.Cell textAlign="right" fontWeight="semibold">{count}</Table.Cell>
                      </Table.Row>
                    ))
                  }
                </Table.Body>
              </Table.Root>
            </Box>
          </Box>
        )}

        {financialBreakdown && hasFinancialBreakdown && (
          <>
            <SectionHeading>Energy Savings & ROI</SectionHeading>
            <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }} gap={4}>
              <StatCard
                label="Total annual savings"
                value={formatCurrency(financialBreakdown.totalAnnualSavings)}
                subtext="energy bill savings identified"
                tooltip="Estimated annual reduction in energy bills across assessed homes with upgrade savings data."
              />
              <StatCard
                label="Avg saving per home"
                value={formatCurrency(financialBreakdown.averageAnnualSavings)}
                subtext={`${financialBreakdown.homesWithSavings} homes with savings data`}
                tooltip="Average annual bill saving across homes where upgrade savings could be calculated."
              />
              <StatCard
                label="Aggregate ROI"
                value={formatRoi(financialBreakdown.roi)}
                subtext={`${financialBreakdown.homesWithRoi} homes with ROI data`}
                tooltip="Annual savings divided by net upgrade cost after applicable grants, aggregated across homes where ROI can be calculated."
              />
              <StatCard
                label="Postcodes covered"
                value={financialBreakdown.postcodeBreakdown.length}
                subtext="outer postcode areas"
              />
            </Grid>

            <Box bg="white" borderRadius="xl" p={{ base: 4, sm: 6 }} border="1px solid" borderColor="gray.200" mt={4}>
              <HStack gap={1} mb={4}>
                <Text fontWeight="semibold" color="gray.700">Savings by outer postcode</Text>
                <HelpIcon tooltip="Outer postcode areas are grouped from assessed homes. Savings are annual estimates; ROI is based on total savings divided by net upgrade cost after applicable grants." />
              </HStack>
              <Box overflowX="auto">
                <Table.Root size="sm" minW="680px">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader>Outer postcode</Table.ColumnHeader>
                      <Table.ColumnHeader textAlign="right">Homes</Table.ColumnHeader>
                      <Table.ColumnHeader textAlign="right">Total annual savings</Table.ColumnHeader>
                      <Table.ColumnHeader textAlign="right">Avg saving / home</Table.ColumnHeader>
                      <Table.ColumnHeader textAlign="right">ROI</Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {financialBreakdown.postcodeBreakdown.map((row) => (
                      <Table.Row key={row.district}>
                        <Table.Cell fontWeight="medium">{row.district}</Table.Cell>
                        <Table.Cell textAlign="right">{row.homes}</Table.Cell>
                        <Table.Cell textAlign="right" fontWeight="semibold">
                          {formatCurrency(row.totalAnnualSavings)}
                        </Table.Cell>
                        <Table.Cell textAlign="right">
                          {formatCurrency(row.averageAnnualSavings)}
                        </Table.Cell>
                        <Table.Cell textAlign="right">
                          <Text as="span" fontWeight="semibold" color={getRoiColor(row.roi)}>
                            {formatRoi(row.roi)}
                          </Text>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              </Box>
            </Box>
          </>
        )}

        {/* Time Series */}
        {hasTimeSeries && (
          <>
            <SectionHeading>Activity Over Time</SectionHeading>
            <Box bg="white" borderRadius="xl" p={{ base: 4, sm: 6 }} border="1px solid" borderColor="gray.200">
              <HStack
                justify="space-between"
                align={{ base: 'stretch', sm: 'center' }}
                gap={3}
                mb={4}
                flexWrap="wrap"
              >
                <HStack gap={1}>
                  <Text fontWeight="semibold" color="gray.700">Activity totals</Text>
                  <HelpIcon tooltip="Daily shows each day. Weekly and monthly add together sessions, assessments, leads, and failed attempts for each period." />
                </HStack>
                <HStack
                  gap={1}
                  bg="gray.100"
                  p={1}
                  borderRadius="lg"
                  alignSelf={{ base: 'stretch', sm: 'auto' }}
                  justify={{ base: 'space-between', sm: 'flex-start' }}
                >
                  {ACTIVITY_GRANULARITY_OPTIONS.map(option => {
                    const isSelected = activityGranularity === option.value
                    return (
                      <Box
                        key={option.value}
                        as="button"
                        type="button"
                        px={{ base: 2, sm: 3 }}
                        py={1.5}
                        borderRadius="md"
                        bg={isSelected ? 'white' : 'transparent'}
                        color={isSelected ? 'gray.900' : 'gray.600'}
                        fontWeight={isSelected ? 'semibold' : 'medium'}
                        fontSize="sm"
                        boxShadow={isSelected ? 'sm' : 'none'}
                        transition="all 120ms ease"
                        aria-pressed={isSelected}
                        onClick={() => setActivityGranularity(option.value)}
                        _hover={{ color: 'gray.900', bg: isSelected ? 'white' : 'gray.50' }}
                        _focusVisible={{ outline: '2px solid', outlineColor: 'blue.400', outlineOffset: '2px' }}
                      >
                        {option.label}
                      </Box>
                    )
                  })}
                </HStack>
              </HStack>
              <Box overflowX="auto">
                <Box minW={{ base: '520px', md: '0' }}>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={activityTimeSeries}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => formatActivityTick(String(value), activityGranularity)}
                      />
                      <YAxis tick={{ fontSize: 12 }} />
                      <RechartsTooltip
                        labelFormatter={(value) => formatActivityTooltipLabel(String(value), activityGranularity)}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="sessions" stroke="#8884d8" strokeWidth={2} name="Sessions" />
                      <Line type="monotone" dataKey="assessments" stroke="#82ca9d" strokeWidth={2} name="Assessments" />
                      <Line type="monotone" dataKey="bookings" stroke="#ffc658" strokeWidth={2} name="Leads" />
                      <Line type="monotone" dataKey="failed" stroke="#e53e3e" strokeWidth={2} name="Failed" />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            </Box>
          </>
        )}

        {/* Geographic Breakdown */}
        {hasGeographic && (
          <>
            <SectionHeading>Geographic Distribution</SectionHeading>
            <Box bg="white" borderRadius="xl" p={{ base: 4, sm: 6 }} border="1px solid" borderColor="gray.200">
              <Box overflowX="auto">
                <Table.Root size="sm" minW="620px">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader>Postcode District</Table.ColumnHeader>
                      <Table.ColumnHeader textAlign="right">Sessions</Table.ColumnHeader>
                      <Table.ColumnHeader textAlign="right">Assessments</Table.ColumnHeader>
                      <Table.ColumnHeader textAlign="right">Leads</Table.ColumnHeader>
                      <Table.ColumnHeader textAlign="right">Conversion</Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {data.geographicBreakdown.slice(0, 10).map((row) => {
                      const convRate = row.sessions > 0 ? ((row.bookings / row.sessions) * 100).toFixed(1) : '0'
                      return (
                        <Table.Row key={row.district}>
                          <Table.Cell fontWeight="medium">{row.district}</Table.Cell>
                          <Table.Cell textAlign="right">{row.sessions}</Table.Cell>
                          <Table.Cell textAlign="right">{row.assessments}</Table.Cell>
                          <Table.Cell textAlign="right">{row.bookings}</Table.Cell>
                          <Table.Cell textAlign="right">
                            <Badge colorPalette={parseFloat(convRate) >= 5 ? 'green' : 'gray'}>
                              {convRate}%
                            </Badge>
                          </Table.Cell>
                        </Table.Row>
                      )
                    })}
                  </Table.Body>
                </Table.Root>
              </Box>
              {data.geographicBreakdown.length > 10 && (
                <Text fontSize="sm" color="gray.500" mt={3}>
                  Showing top 10 of {data.geographicBreakdown.length} districts
                </Text>
              )}
            </Box>
          </>
        )}

        {/* Home Characteristics */}
        {hasHomeData && (
          <>
            <SectionHeading>Home Characteristics</SectionHeading>
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
              <DistributionPieChart
                data={data.homeCharacteristics.epcDistribution}
                title="EPC Rating Distribution"
                colorKey={EPC_COLORS}
                tooltip="Energy Performance Certificate ratings of assessed homes. A is most efficient, G is least. Homes rated D or below typically qualify for grants."
                allLabels={['A', 'B', 'C', 'D', 'E', 'F', 'G']}
              />
              <DistributionPieChart
                data={decamelizeDistributionKeys(data.homeCharacteristics.propertyTypeDistribution)}
                title="Built Form"
                tooltip="Built form of properties assessed through the pilot widget"
                allLabels={['Detached', 'Semi-Detached', 'Mid-Terrace', 'End-Terrace', 'Enclosed Mid-Terrace', 'Enclosed End-Terrace']}
              />
            </Grid>
          </>
        )}

        {/* Engagement */}
        <SectionHeading>Engagement Patterns</SectionHeading>
        <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }} gap={4}>
          <StatCard
            label="Unique visitors"
            value={data.engagement?.uniqueVisitors || 0}
            subtext="by IP address"
            tooltip="Number of distinct IP addresses that accessed the widget. Note: multiple people on the same network will share an IP."
          />
          <StatCard
            label="Bounce rate"
            value={`${data.engagement?.bounceRate || 0}%`}
            subtext="sessions without assessment"
            tooltip="Percentage of sessions where the user left without entering an address or completing an assessment"
          />
          <StatCard
            label="Avg sessions/visitor"
            value={data.engagement?.uniqueVisitors ? (data.totalSessions / data.engagement.uniqueVisitors).toFixed(1) : '0'}
            subtext="engagement depth"
            tooltip="Average number of times each visitor used the widget. Higher values suggest users returning to check multiple properties."
          />
        </Grid>

        {hourData.length > 0 && (
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4} mt={4}>
            <Box bg="white" borderRadius="xl" p={{ base: 4, sm: 6 }} border="1px solid" borderColor="gray.200">
              <HStack gap={1} mb={4}>
                <Text fontWeight="semibold" color="gray.700">Activity by Hour of Day</Text>
                <HelpIcon tooltip="When users are most active. Useful for timing social media posts or council communications." />
              </HStack>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={hourData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" tick={{ fontSize: 10 }} interval={2} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <RechartsTooltip />
                  <Bar dataKey="count" fill="#8884d8" name="Sessions" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
            <Box bg="white" borderRadius="xl" p={{ base: 4, sm: 6 }} border="1px solid" borderColor="gray.200">
              <HStack gap={1} mb={4}>
                <Text fontWeight="semibold" color="gray.700">Activity by Day of Week</Text>
                <HelpIcon tooltip="Which days see the most engagement. Helps identify best days for outreach campaigns." />
              </HStack>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={dayData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <RechartsTooltip />
                  <Bar dataKey="count" fill="#82ca9d" name="Sessions" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Grid>
        )}
      </VStack>
    </Box>
  )
}

export default PilotDashboard
